// Deterministic, seedable RNG so runs can be reproduced/seeded and the sim is
// testable. mulberry32 — tiny, fast, good enough for a game.
export class RNG {
  private s: number

  constructor(seed: number = 0x9e3779b9) {
    this.s = seed >>> 0
    if (this.s === 0) this.s = 0x1a2b3c4d
  }

  /** Re-seed in place. */
  seed(seed: number): void {
    this.s = seed >>> 0
    if (this.s === 0) this.s = 0x1a2b3c4d
  }

  /** Float in [0,1). */
  next(): number {
    this.s |= 0
    this.s = (this.s + 0x6d2b79f5) | 0
    let t = Math.imul(this.s ^ (this.s >>> 15), 1 | this.s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  /** Float in [min,max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min)
  }

  /** Integer in [min,max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1))
  }

  /** True with probability p (0..1). */
  chance(p: number): boolean {
    return this.next() < p
  }

  /** Random element of a non-empty array. */
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)]
  }

  /** Fisher-Yates shuffle (returns a new array). */
  shuffle<T>(arr: readonly T[]): T[] {
    const a = arr.slice()
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1))
      ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
  }

  /**
   * Weighted pick. weights[i] is the relative weight of items[i].
   * Returns the chosen index. Falls back to last index on degenerate input.
   */
  weightedIndex(weights: readonly number[]): number {
    let total = 0
    for (const w of weights) total += Math.max(0, w)
    if (total <= 0) return weights.length - 1
    let r = this.next() * total
    for (let i = 0; i < weights.length; i++) {
      r -= Math.max(0, weights[i])
      if (r < 0) return i
    }
    return weights.length - 1
  }

  /** A random unit-ish direction on the ground plane, as {x,z}. */
  direction(): { x: number; z: number } {
    const a = this.next() * Math.PI * 2
    return { x: Math.cos(a), z: Math.sin(a) }
  }
}

// A shared global RNG for cosmetic/non-deterministic-critical use.
export const fxRng = new RNG(12345)

/**
 * Derive a stable 32-bit numeric seed from a player-typed seed CODE (FNV-1a). Lets runs be shared as
 * memorable strings ("BONKVALHALLA") that reproduce the exact same run. Pure + deterministic.
 */
export function seedFromString(code: string): number {
  let h = 2166136261
  const s = code.trim().toUpperCase()
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) || 0x1a2b3c4d
}

/**
 * The daily-challenge seed for a given calendar day (pass the date string in — e.g. 'YYYY-MM-DD' —
 * so it stays pure/testable; the app supplies today's date). Everyone who plays a day shares the run.
 */
export function dailySeed(dateISO: string): number {
  return seedFromString('DAILY:' + dateISO)
}
